// Lazy-loads hero <video data-lazy-video> elements.
//
// Hero videos can be large and unoptimised (the client uploads whatever they
// have), and they sit at the top of the page where they'd otherwise compete
// with the critical render. The markup keeps the real source in `data-src`
// (see `_components/asset.twig` with `lazy: true`) and shows the poster image
// until this module swaps it in. We only hydrate when the video nears the
// viewport, and never for visitors who prefer reduced motion — for them the
// poster stays as the still alternative and the video file is never fetched.

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

function hydrate(video) {
  if (video.dataset.lazyHydrated) return;

  const sources = video.querySelectorAll("source[data-src]");
  if (!sources.length) return;

  video.dataset.lazyHydrated = "1";
  sources.forEach((source) => {
    source.src = source.dataset.src;
  });

  video.load();
  // Muted autoplay; play() can still be needed after swapping the source in.
  video.play().catch(() => {
    // Autoplay blocked — the poster stays visible, which is fine.
  });
}

const videos = document.querySelectorAll("video[data-lazy-video]");

if (videos.length) {
  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || reduceMotion.matches) return;
          observer.unobserve(entry.target);
          hydrate(entry.target);
        });
      },
      // Start loading a little before the video scrolls into view.
      { rootMargin: "200px" }
    );

    videos.forEach((video) => observer.observe(video));
  } else {
    // No IntersectionObserver → hydrate immediately (still respect motion pref).
    videos.forEach((video) => {
      if (!reduceMotion.matches) hydrate(video);
    });
  }

  // If the visitor turns reduced motion OFF at runtime, hydrate any lazy video
  // that's currently on screen so it can start playing.
  reduceMotion.addEventListener("change", () => {
    if (reduceMotion.matches) return;
    videos.forEach((video) => {
      const rect = video.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (inView) hydrate(video);
    });
  });
}
